/**
 * Queue Management API Service (Admin Only)
 *
 * Provides queue statistics, failed job management,
 * and queue monitoring for system administrators.
 */

import api from './api';

export interface QueueStats {
  failed_count: number;
  recent_count: number;
  throughput: number;
  jobs_per_minute: number;
  horizon_status: 'running' | 'inactive' | 'unknown';
}

export interface FailedJob {
  id: string;
  connection: string;
  queue: string;
  name: string;
  failed_at: string;
  exception: string;
  retried_by: string | null;
}

export interface FailedJobsResponse {
  failed_jobs: FailedJob[];
}

export const queueService = {
  /**
   * Get queue statistics and metrics
   */
  async getStats(): Promise<QueueStats> {
    const response = await api.get('/api/admin/queue/stats');
    return response.data;
  },

  /**
   * Get all failed jobs
   */
  async getFailedJobs(): Promise<FailedJobsResponse> {
    const response = await api.get('/api/admin/queue/failed-jobs');
    return response.data;
  },

  /**
   * Retry a specific failed job
   */
  async retryJob(id: string): Promise<{ message: string }> {
    const response = await api.post(`/api/admin/queue/failed-jobs/${id}/retry`);
    return response.data;
  },

  /**
   * Retry all failed jobs
   */
  async retryAllJobs(): Promise<{ message: string }> {
    const response = await api.post('/api/admin/queue/failed-jobs/retry-all');
    return response.data;
  },

  /**
   * Delete a specific failed job
   */
  async deleteJob(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/queue/failed-jobs/${id}`);
    return response.data;
  },

  /**
   * Flush all failed jobs
   */
  async flushFailedJobs(): Promise<{ message: string }> {
    const response = await api.delete('/api/admin/queue/failed-jobs');
    return response.data;
  },
};
